import { useMemo, useState } from 'react';
import { MdLink } from 'react-icons/md';
import DuplicateMappingModal, { MappingRow } from './DuplicateMappingModal';

// Onglet « À matcher » : to-do des métiers (worktype) Batiweb sur lesquels aucun produit leadrs
// n'est encore mappé. Le matching se crée avec la modale « Dupliquer » de l'onglet Matching produits.

interface Worktype {
    id: number;
    label: string;
}

interface Props {
    worktypes: Worktype[] | null;
    typesLoading: boolean;
    rows: MappingRow[];
    rowsLoading: boolean;
    onMapped: (saved: MappingRow[]) => void;
}

export default function WorktypeTodoTab({ worktypes, typesLoading, rows, rowsLoading, onMapped }: Props) {
    const [search, setSearch] = useState('');
    const [showMatched, setShowMatched] = useState(false);
    const [target, setTarget] = useState<Worktype | null>(null);
    const [success, setSuccess] = useState('');

    // Produits leadrs mappés sur chaque worktype.
    const productsByWorktype = useMemo(() => {
        const map = new Map<number, MappingRow[]>();
        for (const row of rows) {
            if (row.worktypeId != null) map.set(row.worktypeId, [...(map.get(row.worktypeId) || []), row]);
        }
        return map;
    }, [rows]);

    const sorted = useMemo(
        () => (worktypes ? [...worktypes].sort((a, b) => a.label.localeCompare(b.label, 'fr')) : []),
        [worktypes]
    );
    const matchedCount = sorted.filter((w) => productsByWorktype.has(w.id)).length;
    const total = sorted.length;
    const percent = total ? Math.round((matchedCount / total) * 100) : 0;

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return sorted.filter(
            (w) =>
                (showMatched || !productsByWorktype.has(w.id)) &&
                (!q || w.label.toLowerCase().includes(q) || String(w.id) === q)
        );
    }, [sorted, search, showMatched, productsByWorktype]);

    const handleApplied = (saved: MappingRow[]) => {
        onMapped(saved);
        setSuccess(`${target?.label} (${target?.id}) ← ${saved.map((r) => r.subIndustry).join(', ')}`);
        setTarget(null);
    };

    if (!worktypes) {
        return <div className="py-10 text-center text-gray-400">{typesLoading ? 'Chargement...' : 'GET /types indisponible.'}</div>;
    }

    return (
        <>
            <div className="mb-4 rounded-xl border border-gray-200 p-4 dark:border-navy-600">
                <div className="mb-2 flex items-baseline justify-between gap-4">
                    <div className="text-lg font-bold text-navy-700 dark:text-white">
                        {matchedCount} / {total} worktypes Batiweb matchés avec nos produits
                    </div>
                    <div className="text-sm font-semibold text-gray-500">{percent} %</div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                    <div className="h-full rounded-full bg-green-500" style={{ width: `${percent}%` }} />
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {total - matchedCount} métier(s) que Batiweb achète restent sans produit leadrs mappé.
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-4">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un métier ou un id"
                    className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <input type="checkbox" checked={showMatched} onChange={(e) => setShowMatched(e.target.checked)} />
                    Afficher aussi les métiers déjà matchés
                </label>
            </div>

            {success && (
                <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{success}</div>
            )}

            {rowsLoading ? (
                <div className="py-10 text-center text-gray-400">Chargement...</div>
            ) : visible.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                    {search ? 'Aucun métier.' : 'Tous les worktypes Batiweb sont matchés.'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Id</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Métier Batiweb (worktype)</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produits leadrs</th>
                                <th className="pb-3 text-xs font-bold uppercase text-gray-500"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((w) => {
                                const products = productsByWorktype.get(w.id) || [];
                                return (
                                    <tr key={w.id} className="border-b border-gray-100 align-top dark:border-navy-700">
                                        <td className="py-3 pr-4 font-mono text-gray-400">{w.id}</td>
                                        <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">{w.label}</td>
                                        <td className="py-3 pr-4">
                                            {products.length ? (
                                                <span className="text-green-700 dark:text-green-400">
                                                    {products.map((p) => `${p.subIndustry} (${p.industry})`).join(', ')}
                                                </span>
                                            ) : (
                                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">À matcher</span>
                                            )}
                                        </td>
                                        <td className="py-3 text-right">
                                            <button
                                                onClick={() => setTarget(w)}
                                                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                                            >
                                                <MdLink />
                                                {products.length ? 'Ajouter des produits' : 'Matcher'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {target && (
                <DuplicateMappingModal
                    source={{
                        industry: '',
                        subIndustry: '',
                        subIndustryId: null,
                        worktypeId: target.id,
                        worktypeLabel: target.label,
                        note: '',
                        updatedBy: null,
                        updatedAt: null,
                    }}
                    rows={rows}
                    onClose={() => setTarget(null)}
                    onApplied={handleApplied}
                />
            )}
        </>
    );
}
